<template>
  <va-modal
    :value="isOpen"
    id="createOrUpdatePersonModal"
    ref="createOrUpdatePersonModal"
    size="large"
    :noOutsideDismiss="true"
    :okText="$t('modals.buttons.save')"
    :cancelText="$t('modals.buttons.cancel')"
    @ok="submitForm"
    :okDisabled="isSubmitingForm"
    @cancel="onClickCancel"
    >

      <div slot="header" class="modal-title">
        {{
          isUpdating
          ? this.$t('clinical.people.update.title', { name: this.person ? `${this.person.first_name} ${this.person.last_name}` : '' })
          : this.$t('clinical.people.create.title')
        }}
      </div>

      <PersonForm
        :editing="true"
        :selectedPerson="person"
        v-model="form.data.person"
        :formErrors="form.errors"
        :service="service"
      />

    <ConfirmMergeDetailsModal
      v-if="showConfirmMergePersonModal"
      :person="mergePersonInfo"
      @modal-confirmed="handleMergePersonDetails"
      @modal-canceled="showConfirmMergePersonModal = false"
    />

  </va-modal>
</template>
<script>
import Errors from '@/utils/StandardErrors'
import { PersonService } from '@/api/AuthClinicalService'
import PersonForm from '@/components/clinical/people/PersonForm'
import ConfirmationModal from '@/components/common/ConfirmationModal'
import ConfirmMergeDetailsModal from '@/components/clinical/people/ConfirmMergeDetailsModal'
import LocalStorageHelper from "@/local-storage/LocalStorageHelper";
export default {
  props: {
    service: {
      type: Object,
      required: true
    },
    serviceSlug: {
      type: String,
      required: true
    },
    person: {
      type: Object,
      required: false
    }
  },
  components: {
    ConfirmMergeDetailsModal,
    ConfirmationModal,
    PersonForm
  },
  computed: {
    isUpdating: function () {
      return this.person !== null && this.person !== undefined
    },
    canMergeData: function () {
      let supervisorPeoplePermissions = this.service.user_permissions.filter(permission => permission.access_type > 2 && permission.info_type === 100)
      return supervisorPeoplePermissions.length && supervisorPeoplePermissions.length > 0
    }
  },
  data: function () {
    return {
      showConfirmMergePersonModal: false,
      isOpen: false,
      isSubmitingForm: false,
      form: {
        data: {
          person: null
        },
        errors: new Errors()
      },
      mergeFieldsErrors: [],
      mergePersonInfo: null
    }
  },
  mounted: function () {
    this.isOpen = true
  },
  methods: {
    getMergeParams: function () {
      let params = {
        'second_person_file_id': this.person.id,
        'entity_id': this.form.data.person.entity.id,
      }
      for (let key of this.mergeFieldsErrors) {
        params[key] = this.form.data.person[key]
      }

      return params
    },
    getMergeDetails: function () {
      return new Promise((resolve, reject) => {
        let params = this.getMergeParams()

        PersonService.getMergePersonResult(this.service.url, params)
          .then(response => {
            resolve(response)
          })
          .catch(errors => {
            reject(errors)
          })
      })
    },
    handleMergePersonDetails: function () {
      let params = this.getMergeParams()

      PersonService.mergePersonData(this.service.url, params)
        .then(response => {
          //console.log(response.data.data)
          this.showToast(this.$t('clinical.people.merge.success'))
          this.showConfirmMergePersonModal = false
          setTimeout(() => {
            const authClinicalServiceId = this.service.id
            LocalStorageHelper.remove(`auth_clinical_${authClinicalServiceId}_last_patient_page`)
            this.$router.push({ path: `/${this.serviceSlug}/clinical/people` })
          }, 500)
        })
        .catch(errors => {
          this.showToast(this.$t('clinical.people.create.errors.something_went_wrong'), this.$store.state.errorToastOptions)
          this.showConfirmMergePersonModal = false
        })
    },
    submitForm: function () {
      if (!this.isSubmitingForm) {
        this.isSubmitingForm = true
        const formData = this.getFormData()
        const request = this.isUpdating ? PersonService.update(this.service.url, this.person.id, formData) : PersonService.store(this.service.url, formData)
        request
          .then(response => {
            const person = response.data.data
            if (this.isUpdating) {
              this.showToast(this.$t('clinical.people.update.success'), this.$store.state.successToastOptions)
            } else {
              this.showToast(this.$t('clinical.people.create.success'), this.$store.state.successToastOptions)
            }
            if (response.data.error) {
              setTimeout(() => {
                this.showToast(this.$t('clinical.appointments.errors.spice_sync'), this.$store.state.errorToastOptions)
              }, this.$store.state.successToastOptions.duration)
            }
            this.$emit(this.isUpdating ? 'person-updated' : 'person-created', { person })
            this.onClickCancel()
          })
          .catch(errors => {
            if (errors.response && errors.response.status) {
              switch (errors.response.status) {
              case 403:
                this.showToast(this.$t('clinical.people.create.errors.person_forbidden'), this.$store.state.errorToastOptions)
                break
              case 422:
                if (this.canMergeData && this.isUpdating && errors.response.data.errors && this.errorShouldMerge(errors.response.data.errors)) {
                  // Get the data that will remain:
                  this.mergeFieldsErrors = Object.keys(errors.response.data.errors)
                  this.getMergeDetails().then(response => {
                    this.mergePersonInfo = response
                    this.showConfirmMergePersonModal = true
                  })
                }
                this.form.errors.record(errors.response.data.errors, 'people.errors.')
                this.showToast(this.$t('clinical.people.create.errors.person_form_validation_errors'), this.$store.state.errorToastOptions)
                break
              default:
                this.showToast(this.$t('clinical.people.create.errors.something_went_wrong'), this.$store.state.errorToastOptions)
                break
              }
            } else {
              this.showToast(this.$t('clinical.people.create.errors.something_went_wrong'), this.$store.state.errorToastOptions)
            }
          })
          .finally(() => {
            this.isSubmitingForm = false
          })
      }
    },
    errorShouldMerge: function (errors) {
      return (errors['process_id'] && errors['process_id'].some(err => err.replace(/\s/g, '_').toLowerCase() === 'the_process_id_has_already_been_taken.')) ||
        (errors['tax_number'] && errors['tax_number'].some(err => err.replace(/\s/g, '_').toLowerCase() === 'the_tax_number_has_already_been_taken.'))
    },
    onClickCancel: function () {
      this.isOpen = false
      setTimeout(() => {
        this.$emit('cancel')
      }, 500)
    },
    getFormData: function () {
      let formData = JSON.parse(JSON.stringify(this.form.data.person))
      formData.entity_id = formData.entity ? formData.entity.id : null
      formData.gender = formData.gender ? formData.gender.id : null
      delete formData.entity
      return formData
    }
  }
}
</script>
